import type {Language, LocalizedString} from '@cohort/shared/schema/common';
import {z} from 'zod';

export const GOOGLE_FIT_UNKNOWN_ACTIVITY_TYPE = 4;

export const GoogleFitTriggerIntegrationActivityGroups = [
  'RUNNING',
  'CYCLING',
  'WALKING',
  'SWIMMING',
] as const;
export type GoogleFitIntegrationActivityGroupsType =
  (typeof GoogleFitTriggerIntegrationActivityGroups)[number];

export const GoogleFitActivityGroupsSchema = z.enum(GoogleFitTriggerIntegrationActivityGroups);
export type GoogleFitActivityGroupsType = z.infer<typeof GoogleFitActivityGroupsSchema>;

export const GoogleFitActivityTypeSchema = z.number();
export type GoogleFitActivityType = z.infer<typeof GoogleFitActivityTypeSchema>;

export const GoogleFitIntegrationActivityTypesMappedByGroups: Record<
  GoogleFitIntegrationActivityGroupsType,
  GoogleFitActivityType[]
> = {
  RUNNING: [8, 56, 57, 58, 88, 98],
  CYCLING: [1, 14, 15, 16, 17, 18, 19, 25],
  WALKING: [7, 75, 88, 93, 94, 95, 98, 116],
  SWIMMING: [82, 83, 84],
};

export const GoogleFitActivityGroupsTranslations: Record<
  GoogleFitIntegrationActivityGroupsType,
  LocalizedString
> = {
  RUNNING: {en: 'Running', fr: 'Course à pied', es: 'Correr', de: 'Laufen'},
  CYCLING: {en: 'Cycling', fr: 'Cyclisme', es: 'Ciclismo', de: 'Radfahren'},
  WALKING: {en: 'Walking', fr: 'Marche à pied', es: 'Caminata', de: 'Gehen'},
  SWIMMING: {en: 'Swimming', fr: 'Natation', es: 'Natación', de: 'Schwimmen'},
};

export const GoogleFitActivityTypesTranslation: Record<GoogleFitActivityType, LocalizedString> = {
  9: {en: 'Aerobics', fr: 'Aérobic', es: 'Aeróbica', de: 'Aerobic'},
  119: {en: 'Archery', fr: "Tir à l'arc", es: 'Tiro con arco', de: 'Bogenschießen'},
  10: {en: 'Badminton', fr: 'Badminton', es: 'Bádminton', de: 'Badminton'},
  11: {en: 'Baseball', fr: 'Base-ball', es: 'Béisbol', de: 'Baseball'},
  12: {en: 'Basketball', fr: 'Basket-ball', es: 'Básquetbol', de: 'Basketball'},
  13: {en: 'Biathlon', fr: 'Biathlon', es: 'Biatlón', de: 'Biathlon'},
  1: {en: 'Biking', fr: 'Vélo', es: 'Bicicleta', de: 'Radfahren'},
  14: {en: 'Handbiking', fr: 'Vélo', es: 'Ciclismo manual', de: 'Handbiken'},
  15: {
    en: 'Mountain biking',
    fr: 'Vélo tout terrain',
    es: 'Ciclismo de montaña',
    de: 'Mountainbiken',
  },
  16: {en: 'Road biking', fr: 'Vélo de route', es: 'Ciclismo en carretera', de: 'Straßenradfahren'},
  17: {en: 'Spinning', fr: 'Spinning', es: 'Hilado', de: 'Spinning'},
  18: {
    en: 'Stationary biking',
    fr: 'Vélo stationnaire',
    es: 'Bicicleta fija',
    de: 'Standradfahren',
  },
  19: {en: 'Utility biking', fr: 'Vélo utilitaire', es: 'Ciclismo urbano', de: 'Alltagsradfahren'},
  20: {en: 'Boxing', fr: 'Boxe', es: 'Boxeo', de: 'Boxen'},
  21: {en: 'Calisthenics', fr: 'Gymnastique suédoise', es: 'Calistenia', de: 'Calisthenics'},
  22: {
    en: 'Circuit training',
    fr: 'Circuit training',
    es: 'Circuito de entrenamiento',
    de: 'Zirkeltraining',
  },
  23: {en: 'Cricket', fr: 'Cricket', es: 'Cricket', de: 'Cricket'},
  113: {en: 'Crossfit', fr: 'CrossFit', es: 'CrossFit', de: 'CrossFit'},
  106: {en: 'Curling', fr: 'Curling', es: 'Curling', de: 'Curling'},
  24: {en: 'Dancing', fr: 'Danse', es: 'Baile', de: 'Tanzen'},
  102: {en: 'Diving', fr: 'Plongée', es: 'Buceo', de: 'Tauchen'},
  117: {en: 'Elevator', fr: 'Ascenseur', es: 'Ascensor', de: 'Aufzug'},
  25: {en: 'Elliptical', fr: 'Vélo elliptique', es: 'Elíptico', de: 'Crosstrainer'},
  103: {en: 'Ergometer', fr: 'Ergomètre', es: 'Ergómetro', de: 'Ergometer'},
  118: {en: 'Escalator', fr: 'Escalier roulant', es: 'Escalera mecánica', de: 'Rolltreppe'},
  26: {en: 'Fencing', fr: 'Escrime', es: 'Esgrima', de: 'Fechten'},
  27: {
    en: 'Football (American)',
    fr: 'Football américain',
    es: 'Fútbol americano',
    de: 'Football (American)',
  },
  28: {
    en: 'Football (Australian)',
    fr: 'Football australien',
    es: 'Fútbol (Australia)',
    de: 'Football (Australian)',
  },
  29: {en: 'Football (Soccer)', fr: 'Football', es: 'Fútbol', de: 'Fußball'},
  30: {en: 'Frisbee', fr: 'Frisbee', es: 'Frisbee', de: 'Frisbee'},
  31: {en: 'Gardening', fr: 'Jardinage', es: 'Jardinería', de: 'Gartenarbeit'},
  32: {en: 'Golf', fr: 'Golf', es: 'Golf', de: 'Golf'},
  122: {
    en: 'Guided Breathing',
    fr: 'Respiration guidée',
    es: 'Respiración guiada',
    de: 'Geführte Atmung',
  },
  33: {en: 'Gymnastics', fr: 'Gymnastique', es: 'Gimnasia', de: 'Gymnastik'},
  34: {en: 'Handball', fr: 'Handball', es: 'Balonmano', de: 'Handball'},
  114: {en: 'HIIT', fr: 'HIIT', es: 'HIIT', de: 'HIIT'},
  35: {en: 'Hiking', fr: 'Randonnée', es: 'Hacer senderismo', de: 'Wandern'},
  36: {en: 'Hockey', fr: 'Hockey', es: 'Hockey', de: 'Hockey'},
  37: {en: 'Horseback riding', fr: 'À cheval', es: 'Equitación', de: 'Reiten'},
  38: {en: 'Housework', fr: 'Ménage', es: 'Tareas del hogar', de: 'Hausarbeit'},
  104: {en: 'Ice skating', fr: 'Patin à glace', es: 'Patinaje sobre hielo', de: 'Eislaufen'},
  0: {en: 'In vehicle', fr: 'Dans un véhicule', es: 'En un vehículo', de: 'Im Fahrzeug'},
  115: {
    en: 'Interval Training',
    fr: 'Entraînement fractionné',
    es: 'Entrenamiento por intervalos',
    de: 'Intervalltraining',
  },
  39: {en: 'Jumping rope', fr: 'Corde à sauter', es: 'Saltar la cuerda', de: 'Seilspringen'},
  40: {en: 'Kayaking', fr: 'Canoë-kayak', es: 'Kayakismo', de: 'Kajakfahren'},
  41: {
    en: 'Kettlebell training',
    fr: 'Entraînement avec kettlebell',
    es: 'Entrenamiento con pesas rusas',
    de: 'Kettlebell-Training',
  },
  42: {en: 'Kickboxing', fr: 'Kick-boxing', es: 'Kickboxing', de: 'Kickboxen'},
  43: {en: 'Kitesurfing', fr: 'Kitesurf', es: 'Kitesurf', de: 'Kitesurfen'},
  44: {en: 'Martial arts', fr: 'Arts martiaux', es: 'Artes marciales', de: 'Kampfsport'},
  45: {en: 'Meditation', fr: 'Méditation', es: 'Meditación', de: 'Meditation'},
  46: {
    en: 'Mixed martial arts',
    fr: 'Arts martiaux mixtes',
    es: 'Artes marciales mixtas',
    de: 'Mixed Martial Arts',
  },
  108: {
    en: 'Other (unclassified fitness activity)',
    fr: 'Autre (activité de remise en forme non classée)',
    es: 'Otro (actividad de entrenamiento no clasificada)',
    de: 'Sonstiges (nicht klassifizierte Fitnessaktivität)',
  },
  47: {en: 'P90X exercises', fr: 'Exercices P90X', es: 'Ejercicios de P90X', de: 'P90X-Übungen'},
  48: {en: 'Paragliding', fr: 'Parapente', es: 'Parapente', de: 'Gleitschirmfliegen'},
  49: {en: 'Pilates', fr: 'Pilates', es: 'Pilates', de: 'Pilates'},
  50: {en: 'Polo', fr: 'Polo', es: 'Polo', de: 'Polo'},
  51: {en: 'Racquetball', fr: 'Racketball', es: 'Ráquetbol', de: 'Racquetball'},
  52: {en: 'Rock climbing', fr: 'Escalade', es: 'Escalada', de: 'Klettern'},
  53: {en: 'Rowing', fr: 'Aviron', es: 'Remo', de: 'Rudern'},
  54: {en: 'Rowing machine', fr: 'Rameur', es: 'Máquina de remo', de: 'Rudergerät'},
  55: {en: 'Rugby', fr: 'Rugby', es: 'Rugby', de: 'Rugby'},
  8: {en: 'Running', fr: 'Running', es: 'Activo', de: 'Laufen'},
  56: {en: 'Jogging', fr: 'Jogging', es: 'Trote', de: 'Joggen'},
  57: {
    en: 'Running on sand',
    fr: 'Course sur sable',
    es: 'Correr en la arena',
    de: 'Laufen im Sand',
  },
  58: {
    en: 'Running (treadmill)',
    fr: 'Course à pied (tapis de course)',
    es: 'Correr (correr)',
    de: 'Laufen (Laufband)',
  },
  59: {en: 'Sailing', fr: 'Voile', es: 'Navegar', de: 'Segeln'},
  60: {en: 'Scuba diving', fr: 'Plongée sous-marine', es: 'Buceo', de: 'Gerätetauchen'},
  61: {en: 'Skateboarding', fr: 'Skateboard', es: 'Patineta', de: 'Skateboarden'},
  62: {en: 'Skating', fr: 'Patinage', es: 'Patinaje', de: 'Skaten'},
  63: {en: 'Cross skating', fr: 'Cross-skating', es: 'Patinaje nórdico', de: 'Cross-Skating'},
  105: {
    en: 'Indoor skating',
    fr: 'Patinage en salle',
    es: 'Patinaje en pista cubierta',
    de: 'Indoor-Skaten',
  },
  64: {
    en: 'Inline skating (rollerblading)',
    fr: 'Roller en ligne',
    es: 'Patinaje en línea (patinaje)',
    de: 'Inlineskaten (Rollerblading)',
  },
  65: {en: 'Skiing', fr: 'Ski', es: 'Esquí', de: 'Skifahren'},
  66: {en: 'Back-country skiing', fr: 'Ski de fond', es: 'Esquí de fondo', de: 'Tourenskilauf'},
  67: {en: 'Cross-country skiing', fr: 'Ski de fond', es: 'Esquí de fondo', de: 'Skilanglauf'},
  68: {en: 'Downhill skiing', fr: 'Ski alpin', es: 'Esquí cuesta abajo', de: 'Abfahrtsski'},
  69: {en: 'Kite skiing', fr: 'Snowkite', es: 'Kite esquí', de: 'Snowkiten'},
  70: {en: 'Roller skiing', fr: 'Ski à roulettes', es: 'Skiroll', de: 'Rollski'},
  71: {en: 'Sledding', fr: 'Luge', es: 'Carrera en trineo', de: 'Schlittenfahren'},
  73: {en: 'Snowboarding', fr: 'Snowboard', es: 'Snowboarding', de: 'Snowboarden'},
  74: {en: 'Snowmobile', fr: 'Motoneige', es: 'Moto de nieve', de: 'Schneemobil'},
  75: {
    en: 'Snowshoeing',
    fr: 'Randonnée en raquettes',
    es: 'Caminata con raquetas para nieve',
    de: 'Schneeschuhwandern',
  },
  120: {en: 'Softball', fr: 'Softball', es: 'Sóftbol', de: 'Softball'},
  76: {en: 'Squash', fr: 'Squash', es: 'Squash', de: 'Squash'},
  77: {en: 'Stair climbing', fr: 'Escaliers', es: 'Subir escaleras', de: 'Treppensteigen'},
  78: {
    en: 'Stair-climbing machine',
    fr: "Machine de montée d'escaliers",
    es: 'Máquina escaladora',
    de: 'Treppensteiger-Maschine',
  },
  79: {
    en: 'Stand-up paddleboarding',
    fr: 'Stand up paddle',
    es: 'Surf de remo',
    de: 'Stand-up-Paddling',
  },
  3: {
    en: 'Still (not moving)',
    fr: 'Toujours (immobile)',
    es: 'Todavía (no se mueve)',
    de: 'Still (keine Bewegung)',
  },
  80: {
    en: 'Strength training',
    fr: 'Musculation',
    es: 'Entrenamiento de fuerza',
    de: 'Krafttraining',
  },
  81: {en: 'Surfing', fr: 'Surf', es: 'Surf', de: 'Surfen'},
  82: {en: 'Swimming', fr: 'Natation', es: 'Natación', de: 'Schwimmen'},
  84: {
    en: 'Swimming (open water)',
    fr: 'Natation (eau libre)',
    es: 'Natación (agua abierta)',
    de: 'Schwimmen (Freiwasser)',
  },
  83: {
    en: 'Swimming (swimming pool)',
    fr: 'Natation (piscine)',
    es: 'Natación (piscina)',
    de: 'Schwimmen (Schwimmbad)',
  },
  85: {
    en: 'Table tennis (ping pong)',
    fr: 'Tables de ping-pong',
    es: 'Tenis de mesa (ping pong)',
    de: 'Tischtennis (Ping Pong)',
  },
  86: {en: 'Team sports', fr: "Sports d'équipe", es: 'Deportes en equipo', de: 'Mannschaftssport'},
  87: {en: 'Tennis', fr: 'Tennis', es: 'Tenis', de: 'Tennis'},
  5: {
    en: 'Tilting (sudden device gravity change)',
    fr: 'Inclinaison (changement de gravité des appareils soudains)',
    es: 'Inclinación (cambio repentino de gravedad del dispositivo)',
    de: 'Neigen (plötzliche Änderung der Gerätelage)',
  },
  88: {
    en: 'Treadmill (walking or running)',
    fr: 'Tapis de course (marche ou course)',
    es: 'Correr en cinta (caminar o correr)',
    de: 'Laufband (Gehen oder Laufen)',
  },
  4: {
    en: 'Unknown (unable to detect activity)',
    fr: "Inconnu (impossible de détecter l'activité)",
    es: 'Desconocido (no se puede detectar la actividad)',
    de: 'Unbekannt (Aktivität konnte nicht erkannt werden)',
  },
  89: {en: 'Volleyball', fr: 'Volley-ball', es: 'Voleibol', de: 'Volleyball'},
  90: {
    en: 'Volleyball (beach)',
    fr: 'Volley-ball (plage)',
    es: 'Voleibol (playa)',
    de: 'Volleyball (Beach)',
  },
  91: {
    en: 'Volleyball (indoor)',
    fr: 'Volley-ball (intérieur)',
    es: 'Voleibol (para interiores)',
    de: 'Volleyball (Halle)',
  },
  92: {en: 'Wakeboarding', fr: 'Wakeboard', es: 'Wakeboarding', de: 'Wakeboarding'},
  7: {en: 'Walking', fr: 'Marche', es: 'A pie', de: 'Gehen'},
  93: {
    en: 'Walking (fitness)',
    fr: 'Marche (fitness)',
    es: 'A pie (deporte)',
    de: 'Gehen (Fitness)',
  },
  94: {
    en: 'Nording walking',
    fr: 'Marche nordique',
    es: 'Caminata hacia el norte',
    de: 'Nordic Walking',
  },
  95: {
    en: 'Walking (treadmill)',
    fr: 'À pied (tapis de course)',
    es: 'Caminar (correr)',
    de: 'Gehen (Laufband)',
  },
  116: {
    en: 'Walking (stroller)',
    fr: 'À pied (poussette)',
    es: 'A pie (stroller)',
    de: 'Gehen (Kinderwagen)',
  },
  96: {en: 'Waterpolo', fr: 'Waterpolo', es: 'Waterpolo', de: 'Wasserball'},
  97: {en: 'Weightlifting', fr: 'Haltérophilie', es: 'Levantamiento de pesas', de: 'Gewichtheben'},
  98: {en: 'Wheelchair', fr: 'Fauteuil roulant', es: 'Silla de ruedas', de: 'Rollstuhl'},
  99: {en: 'Windsurfing', fr: 'Planche à voile', es: 'Windsurf', de: 'Windsurfen'},
  100: {en: 'Yoga', fr: 'Yoga', es: 'Yoga', de: 'Yoga'},
  101: {en: 'Zumba', fr: 'Zumba', es: 'Zumba', de: 'Zumba'},
};

export function getGoogleFitActivityGroupLabel(
  activityGroup: GoogleFitIntegrationActivityGroupsType,
  language: Language
): string {
  return (
    GoogleFitActivityGroupsTranslations[activityGroup][language] ??
    GoogleFitActivityGroupsTranslations[activityGroup].en ??
    'Unknown'
  );
}
export function getGoogleFitActivityTypeLabel(
  activityType: GoogleFitActivityType,
  language: Language
): string {
  return (
    GoogleFitActivityTypesTranslation[activityType]?.[language] ??
    GoogleFitActivityTypesTranslation[activityType]?.en ??
    'Unknown'
  );
}

export const GoogleFitActivitySchema = z.object({
  id: z.string(),
  distanceInMeters: z.number(),
  durationInSeconds: z.number(),
  startDate: z.date(),
  steps: z.number(),
  speedInMetersPerSecond: z.number(),
  type: GoogleFitActivityTypeSchema,
});

export type GoogleFitActivity = z.infer<typeof GoogleFitActivitySchema>;
